// src/pages/TermsAndConditions.jsx
import React from "react";

export default function TermsAndConditions() {
  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "24px" }}>
      <h1>Términos y Condiciones de Uso de MyShopCity</h1>
      <p><strong>Última actualización:</strong> 12/11/2025</p>

      <p>
        Estos Términos y Condiciones regulan el uso de la aplicación móvil, la web
        y el panel administrativo de <strong>MyShopCity</strong>. Al acceder o utilizar
        la plataforma, usted acepta cumplir con todas las disposiciones aquí descritas.
      </p>

      <h2>1. Definiciones</h2>
      <ul>
        <li><strong>Plataforma:</strong> MyShopCity y sus servicios.</li>
        <li><strong>Usuario:</strong> Persona que compra productos.</li>
        <li><strong>Vendedor:</strong> Comercio o persona que publica productos.</li>
        <li><strong>Repartidor:</strong> Persona designada para realizar entregas locales.</li>
        <li><strong>Pedido:</strong> Compra realizada dentro de la plataforma.</li>
        <li><strong>Stripe:</strong> Plataforma externa utilizada para procesar pagos.</li>
      </ul>

      <h2>2. Servicios de MyShopCity</h2>
      <p>MyShopCity es una plataforma que:</p>
      <ul>
        <li>Conecta compradores con vendedores locales.</li>
        <li>Permite explorar productos, agregarlos al carrito y realizar compras.</li>
        <li>Gestiona pagos de manera segura mediante Stripe.</li>
        <li>Ofrece chat entre usuario y vendedor.</li>
        <li>
          Organiza entregas rápidas dentro de la ciudad mediante rutas preestablecidas.
        </li>
        <li>Facilita herramientas de administración a tiendas locales.</li>
      </ul>
      <p>
        MyShopCity <strong>no es propietaria de los productos</strong> ni realiza ventas
        directas. Cada vendedor es responsable del contenido, precio, autenticidad y
        calidad del producto ofrecido.
      </p>

      <h2>3. Registro de usuario</h2>
      <p>Para utilizar la app, el usuario debe registrar:</p>
      <ul>
        <li>Nombre</li>
        <li>Dirección</li>
        <li>Teléfono</li>
        <li>Correo electrónico</li>
        <li>Ciudad</li>
      </ul>
      <p>Los datos deben ser reales y actualizados.</p>

      <h2>4. Registro de vendedores</h2>
      <p>Los vendedores deben completar un proceso de verificación que incluye:</p>
      <ul>
        <li>Identificación oficial</li>
        <li>Comprobante de domicilio</li>
        <li>Datos reales de la tienda</li>
        <li>Cuenta Stripe Connect</li>
      </ul>
      <p>MyShopCity puede rechazar o eliminar cuentas de vendedores que:</p>
      <ul>
        <li>Publiquen contenido engañoso.</li>
        <li>Ofrezcan productos prohibidos o falsificados.</li>
        <li>Incumplan políticas de calidad.</li>
        <li>Abusen del sistema de envíos o devoluciones.</li>
      </ul>

      <h2>5. Publicación de productos</h2>
      <p>El vendedor está obligado a:</p>
      <ul>
        <li>Proporcionar descripciones reales y precisas.</li>
        <li>Publicar imágenes auténticas del producto.</li>
        <li>Indicar variantes, colores y detalles importantes.</li>
        <li>Establecer el precio base (lo que el vendedor desea recibir).</li>
        <li>
          Aceptar que el precio final incluirá:
          <ul>
            <li>15% de comisión de la plataforma.</li>
            <li>Costo de envío.</li>
          </ul>
        </li>
      </ul>
      <p>
        El precio visible al usuario será mayor al precio base del vendedor por estos
        conceptos.
      </p>

      <h2>6. Proceso de compra y pagos</h2>

      <h3>6.1 Pago seguro</h3>
      <p>
        Todas las compras se procesan mediante Stripe, un proveedor internacional de
        pagos seguros.
      </p>

      <h3>6.2 Retención de ingresos</h3>
      <p>Cuando un usuario compra un producto:</p>
      <ul>
        <li>Stripe retiene el pago durante <strong>2 días</strong>.</li>
        <li>El usuario verifica que el producto cumple lo prometido.</li>
        <li>Si todo es correcto, el pago se libera al vendedor automáticamente.</li>
      </ul>

      <h3>6.3 Reclamaciones</h3>
      <p>El usuario puede abrir una reclamación dentro de los 2 días si:</p>
      <ul>
        <li>El producto no coincide con la descripción.</li>
        <li>El producto está incompleto, dañado o es engañoso.</li>
        <li>El vendedor omitió información.</li>
      </ul>
      <p>En caso de reclamo válido:</p>
      <ul>
        <li>El producto debe ser devuelto en el mismo estado que fue entregado.</li>
        <li>
          Si se manipula, altera o usa indebidamente, <strong>no se otorgará
          reembolso</strong>.
        </li>
        <li>
          Si el vendedor incumplió, MyShopCity podrá:
          <ul>
            <li>Reembolsar al comprador.</li>
            <li>Descontar el pago al vendedor.</li>
            <li>Bloquear su cuenta por malas prácticas.</li>
          </ul>
        </li>
      </ul>
      <p>En reclamos no válidos (capricho, arrepentimiento, mal uso):</p>
      <ul>
        <li>No se otorgará reembolso.</li>
        <li>El pago se liberará al vendedor.</li>
      </ul>

      <h2>7. Entregas</h2>
      <p>
        Los envíos se realizan mediante rutas definidas dentro de la aplicación. Los tiempos
        dependen de la disponibilidad y del volumen del día.
      </p>
      <p>El comprador debe proporcionar:</p>
      <ul>
        <li>Dirección completa.</li>
        <li>Teléfono correcto.</li>
        <li>Disponibilidad para recibir.</li>
      </ul>

      <h2>8. Responsabilidades del usuario</h2>
      <p>El usuario se compromete a:</p>
      <ul>
        <li>Usar la app de forma legal.</li>
        <li>No abusar del sistema de reclamos.</li>
        <li>Recibir el producto en el horario acordado.</li>
        <li>Devolver el producto en caso de reclamo.</li>
        <li>No realizar fraudes o compras falsas.</li>
      </ul>

      <h2>9. Responsabilidades del vendedor</h2>
      <p>El vendedor debe garantizar:</p>
      <ul>
        <li>Productos reales y en buen estado.</li>
        <li>Descripciones verídicas.</li>
        <li>Entrega puntual del producto al repartidor.</li>
        <li>Responder dudas mediante el chat.</li>
        <li>Trato respetuoso y profesional.</li>
      </ul>

      <h2>10. Comisión de MyShopCity</h2>
      <p>
        La plataforma cobra <strong>15% del costo total del producto</strong>. Este monto
        se descuenta automáticamente antes de enviar el pago al vendedor.
      </p>

      <h2>11. Suspensión o cancelación de cuentas</h2>
      <p>
        MyShopCity podrá suspender cuentas en casos como:
      </p>
      <ul>
        <li>Fraude o actividad sospechosa.</li>
        <li>Publicación de productos engañosos.</li>
        <li>Falta de cumplimiento con las políticas de calidad.</li>
        <li>Mal uso de reembolsos.</li>
        <li>Información falsa del vendedor.</li>
      </ul>

      <h2>12. Propiedad intelectual</h2>
      <p>
        Todo contenido relacionado con MyShopCity (diseño, marca, software, logos) es
        propiedad exclusiva de la plataforma.
      </p>

      <h2>13. Limitación de responsabilidad</h2>
      <p>MyShopCity no es responsable por:</p>
      <ul>
        <li>Productos defectuosos vendidos por terceros.</li>
        <li>Información falsa publicada por vendedores.</li>
        <li>Problemas externos de Stripe.</li>
        <li>Retrasos por causas de fuerza mayor.</li>
        <li>Daños indirectos derivados del uso de la plataforma.</li>
      </ul>

      <h2>14. Modificaciones</h2>
      <p>
        Podemos actualizar estos Términos y Condiciones en cualquier momento. Las nuevas
        versiones serán publicadas en la app y el sitio web.
      </p>

      <h2>15. Contacto</h2>
      <p>
        Para dudas legales o soporte puede escribir a:<br />
        📧 <a href="myshopcity@gmail.com">myshopcity@gmail.com</a>
      </p>
    </div>
  );
}
